import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { CanvaStatus, CanvaSyncSettingsDto } from "shared";
import { Api } from "../api.config";
import { useCompany } from "../company/useCompany";
import { LinkGen, QueryKeys } from "../LinkGen";

function CanvaSyncSettingsPanel(props: { settings: CanvaSyncSettingsDto }) {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();

	const updateSettings = useMutation({
		mutationFn: async (patch: Partial<CanvaSyncSettingsDto>) => {
			const res = await Api.CanvaUpdateSyncSettings(patch);
			return res.data;
		},
		onSuccess: (data) => {
			queryClient.setQueryData(
				QueryKeys.Integrations.Canva.Status(companyId),
				(prev: CanvaStatus | undefined) =>
					prev ? { ...prev, syncSettings: data } : prev,
			);
		},
	});

	const toggle = (key: keyof CanvaSyncSettingsDto) => {
		updateSettings.mutate({ [key]: !props.settings[key] });
	};

	return (
		<div className="canva-settings__checks">
			<Form.Check
				type="switch"
				id="canva-sync-return"
				label="Sync on return from editor"
				checked={props.settings.autoSyncOnReturn}
				disabled={updateSettings.isPending}
				onChange={() => toggle("autoSyncOnReturn")}
			/>
			<Form.Check
				type="switch"
				id="canva-sync-bulk"
				label="Update all linked copies"
				checked={props.settings.bulkReplaceOnUpdate}
				disabled={updateSettings.isPending}
				onChange={() => toggle("bulkReplaceOnUpdate")}
			/>
			<Form.Check
				type="switch"
				id="canva-sync-poll"
				label="Hourly update check"
				checked={props.settings.pollForUpdates}
				disabled={updateSettings.isPending}
				onChange={() => toggle("pollForUpdates")}
			/>
		</div>
	);
}

export function CanvaSettingsPanel(props: { status: CanvaStatus }) {
	const [syncOpen, setSyncOpen] = useState(false);
	const { status } = props;

	return (
		<div className="canva-settings">
			<div className="app-connect-page-selection">
				Account
				<span className="active">{status.profile?.name ?? "Connected"}</span>
			</div>

			{status.teamId && (
				<p className="canva-settings__hint">Team workspace connected</p>
			)}
			{status.lastSyncAt && (
				<p className="canva-settings__hint">
					Last sync {new Date(status.lastSyncAt).toLocaleString()}
				</p>
			)}
			{status.tokenExpired && (
				<p className="canva-settings__hint canva-settings__hint--error">
					Session expired — reconnect to import.
				</p>
			)}

			{status.syncSettings && (
				<div className="canva-settings__sync">
					<button
						type="button"
						className="canva-settings__sync-toggle"
						onClick={() => setSyncOpen((open) => !open)}
						aria-expanded={syncOpen}
					>
						Sync options {syncOpen ? "▴" : "▾"}
					</button>
					{syncOpen && (
						<CanvaSyncSettingsPanel settings={status.syncSettings} />
					)}
				</div>
			)}

			<Link
				to={LinkGen.Settings.CanvaAudit()}
				className="canva-settings__audit-link"
			>
				View activity log →
			</Link>
		</div>
	);
}
